import { batch, createEffect, onCleanup, onMount, type Accessor } from "solid-js"
import { produce, type SetStoreFunction, type Store } from "solid-js/store"
import { DevicePreference, removePersisted } from "@/storage/persist"
import { createScrollPersistence } from "../layout-scroll"
import { ensureSessionKey, parseLayoutScopeKey, pruneSessionKeys } from "../layout-model"
import type { LayoutState } from "./state"

const MAX_SESSION_KEYS = 50

export function createLayoutSessionState(input: {
  store: Store<LayoutState>
  setStore: SetStoreFunction<LayoutState>
  ready: Accessor<boolean>
}) {
  const usage = {
    active: undefined as string | undefined,
    pruned: false,
    used: new Map<string, number>(),
  }
  const sessionStateKeys = ["terminal", "file-view"] as const

  const dropPersistedState = (keys: string[]) => {
    for (const key of keys) {
      const scope = parseLayoutScopeKey(key)
      if (!scope) continue
      const { workspaceID: directory, sessionID: session } = scope
      for (const entry of sessionStateKeys) {
        const target = session
          ? DevicePreference.session(directory, session, entry)
          : DevicePreference.workspace(directory, entry)
        void removePersisted(target)
      }
    }
  }

  const scroll = createScrollPersistence({
    debounceMs: 250,
    getSnapshot: (sessionKey) => input.store.sessionView[sessionKey]?.scroll,
    onFlush: (sessionKey, next) => {
      const current = input.store.sessionView[sessionKey]
      if (!current) input.setStore("sessionView", sessionKey, { scroll: next })
      else input.setStore("sessionView", sessionKey, "scroll", (previous) => ({ ...(previous ?? {}), ...next }))
      prune(usage.active ?? sessionKey)
    },
  })

  function prune(keep?: string) {
    const dropped = pruneSessionKeys({
      keep,
      max: MAX_SESSION_KEYS,
      used: usage.used,
      view: Object.keys(input.store.sessionView),
      tabs: Object.keys(input.store.sessionTabs),
    })
    if (dropped.length === 0) return
    input.setStore(produce((draft) => {
      for (const key of dropped) {
        delete draft.sessionView[key]
        delete draft.sessionTabs[key]
      }
    }))
    scroll.drop(dropped)
    dropPersistedState(dropped)
    for (const key of dropped) usage.used.delete(key)
  }

  function touch(key: string) {
    usage.active = key
    usage.used.set(key, Date.now())
    if (!input.ready() || usage.pruned) return
    usage.pruned = true
    prune(key)
  }

  createEffect(() => {
    if (!input.ready() || usage.pruned || !usage.active) return
    usage.pruned = true
    prune(usage.active)
  })

  onMount(() => {
    const flush = () => batch(() => scroll.flushAll())
    const hidden = () => {
      if (document.visibilityState === "hidden") flush()
    }
    window.addEventListener("pagehide", flush)
    document.addEventListener("visibilitychange", hidden)
    onCleanup(() => {
      window.removeEventListener("pagehide", flush)
      document.removeEventListener("visibilitychange", hidden)
      scroll.dispose()
    })
  })

  return {
    scroll,
    touch,
    ensureKey: (key: string) => ensureSessionKey(key, touch, (sessionKey) => scroll.seed(sessionKey)),
    prune,
  }
}
